/**
 * Resolves the app's per-environment configuration.
 *
 * The two environment names are fixed repo-wide: `local`, `production`.
 * Values arrive from Vite's env files (`.env`, `.env.production`) and are validated here
 * rather than trusted, so a missing or malformed value fails at startup with a message
 * that says what to fix — not later, at the first request.
 */

export const ENVIRONMENTS = ['local', 'production'] as const

export type Environment = (typeof ENVIRONMENTS)[number]

export interface AppConfig {
  readonly environment: Environment
  readonly apiBaseUrl: string
  /** Google OAuth client id — a public value. Null means sign-in is not configured yet. */
  readonly googleClientId: string | null
}

function isEnvironment(value: unknown): value is Environment {
  return typeof value === 'string' && (ENVIRONMENTS as readonly string[]).includes(value)
}

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url
}

export function resolveConfig(source: Record<string, unknown>): AppConfig {
  const environment = source.VITE_GYMCLOUD_ENV

  if (!isEnvironment(environment)) {
    throw new Error(
      `VITE_GYMCLOUD_ENV must be one of ${ENVIRONMENTS.join(', ')}, got ${JSON.stringify(environment)}`,
    )
  }

  const rawBaseUrl = source.VITE_API_BASE_URL

  if (typeof rawBaseUrl !== 'string' || rawBaseUrl.length === 0) {
    throw new Error(
      `VITE_API_BASE_URL must be a non-empty string, got ${JSON.stringify(rawBaseUrl)}`,
    )
  }

  let parsed: URL
  try {
    parsed = new URL(rawBaseUrl)
  } catch {
    throw new Error(`VITE_API_BASE_URL must be an absolute URL, got ${JSON.stringify(rawBaseUrl)}`)
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error(`VITE_API_BASE_URL must use http or https, got ${JSON.stringify(rawBaseUrl)}`)
  }

  // Cleartext is a local-development affordance only; it must never reach a deployed environment.
  if (environment !== 'local' && parsed.protocol !== 'https:') {
    throw new Error(`VITE_API_BASE_URL must use https in the ${environment} environment`)
  }

  const rawClientId = source.VITE_GOOGLE_CLIENT_ID
  const googleClientId = typeof rawClientId === 'string' && rawClientId !== '' ? rawClientId : null

  return {
    environment,
    apiBaseUrl: stripTrailingSlash(rawBaseUrl),
    googleClientId,
  }
}

// Vite types env values loosely, so they are validated here rather than trusted.
export const config: AppConfig = resolveConfig(import.meta.env)
